import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { MarkdownHtmlPipe } from '../shared/markdown-html.pipe';
import { ContentCategory, ContentService, FaqItem } from '../widgets/content/content.service';

@Component({
  selector: 'app-faq-page',
  imports: [MarkdownHtmlPipe, RouterLink],
  template: `
    <section class="content-hero">
      <p class="eyebrow">SSS</p>
      <h1>{{ category()?.title || copy().title }}</h1>
      <p class="lead">
        @if (category()) {
          {{ copy().categoryLead }}
        } @else if (fallbackFaqs().length) {
          <article class="article-card">
            @for (faq of fallbackFaqs(); track faq.title) {
              <details>
                <summary>{{ faq.title }}</summary>
                <div class="markdown" [innerHTML]="faq.content | markdownHtml"></div>
              </details>
            }
          </article>
        } @else {
          {{ copy().lead }}
        }
      </p>
      @if (category()) {
        <a class="pill-link" [routerLink]="i18n.routePath('faq')">{{ copy().allCategories }}</a>
      }
    </section>

    <section class="content-shell">
      @if (!slug()) {
        @if (categories().length) {
          <div class="content-grid">
            @for (item of categories(); track item.id) {
              <a class="content-card" [routerLink]="i18n.routePath('faqCategory', { slug: item.slug })">
                <span>{{ copy().category }}</span>
                <h2>{{ item.title }}</h2>
                <p>{{ copy().openCategory }}</p>
              </a>
            }
          </div>
        } @else if (!fallbackFaqs().length) {
          <article class="empty-card">
            <h2>{{ copy().noCategories }}</h2>
            <p>{{ copy().noCategoriesLead }}</p>
          </article>
        }
      } @else {
        <article class="article-card">
          @if (faqs().length) {
            @for (faq of faqs(); track faq.id) {
              <details [attr.id]="'faq-' + faq.id">
                <summary>{{ faq.title }}</summary>
                <div class="markdown" [innerHTML]="faq.content | markdownHtml"></div>
              </details>
            }
          } @else {
            <h2>{{ copy().noFaqs }}</h2>
            <p>{{ copy().noFaqsLead }}</p>
          }
        </article>
      }
    </section>
  `,
  styles: [`
    .content-hero,
    .content-shell {
      margin: 0 auto;
      max-width: 1180px;
    }

    .content-hero {
      background:
        radial-gradient(circle at 78% 0%, var(--glow), transparent 32%),
        linear-gradient(135deg, var(--surface), color-mix(in srgb, var(--accent) 10%, var(--surface)));
      border: 1px solid var(--border);
      border-radius: 42px;
      box-shadow: 0 30px 80px var(--shadow);
      padding: clamp(34px, 7vw, 78px);
    }

    .content-shell {
      margin-top: 22px;
    }

    .content-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .content-card,
    .article-card,
    .empty-card {
      background:
        radial-gradient(circle at 20% 0%, color-mix(in srgb, var(--accent) 18%, transparent), transparent 14rem),
        var(--card);
      border: 1px solid var(--border);
      border-radius: 30px;
      box-shadow: 0 20px 58px color-mix(in srgb, var(--shadow) 70%, transparent);
    }

    .content-card {
      color: var(--ink);
      display: grid;
      gap: 12px;
      min-height: 180px;
      padding: 24px;
      text-decoration: none;
    }

    .content-card span,
    .eyebrow {
      color: var(--accent);
      font-size: 12px;
      font-weight: 950;
      letter-spacing: .14em;
      text-transform: uppercase;
    }

    .content-card h2,
    .article-card h2,
    .empty-card h2 {
      color: var(--ink);
      letter-spacing: -.04em;
      margin: 0;
    }

    .content-card p,
    .lead,
    .empty-card p {
      color: var(--muted);
      line-height: 1.6;
    }

    .pill-link {
      background: var(--ink);
      border-radius: 999px;
      color: var(--paper);
      display: inline-flex;
      font-weight: 950;
      margin-top: 20px;
      padding: 12px 16px;
      text-decoration: none;
    }

    .article-card,
    .empty-card {
      padding: clamp(24px, 5vw, 46px);
    }

    details {
      border-top: 1px solid var(--border);
      padding: 18px 0;
    }

    details:first-child {
      border-top: 0;
    }

    summary {
      color: var(--ink);
      cursor: pointer;
      font-size: 20px;
      font-weight: 950;
    }

    .markdown {
      color: var(--muted);
      line-height: 1.7;
      margin-top: 12px;
    }

    @media (max-width: 900px) {
      .content-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class FaqPage {
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);
  private readonly content = inject(ContentService);
  private readonly seo = inject(SeoService);
  private readonly site = inject(SiteConfigService);
  protected readonly slug = signal('');
  protected readonly category = signal<ContentCategory | null>(null);
  protected readonly categories = signal<ContentCategory[]>([]);
  protected readonly faqs = signal<FaqItem[]>([]);
  protected readonly fallbackFaqs = computed<FaqItem[]>(() => {
    if (this.site.config().brandKey !== 'bdsmbeds') return [];
    const rows: Record<string, Array<[string,string]>> = {
      en: [
        ['How can I choose the right product?', 'Review the dimensions, materials and options on the product page. Contact support before ordering if you need help comparing models.'],
        ['How are orders delivered?', 'Available delivery methods, timing and charges are shown during checkout and in your order details.'],
        ['Can I return an order?', 'Return eligibility depends on the product and its condition. Review the published return information before ordering; unless a product states otherwise, the customer pays return shipping.'],
        ['How can I get help with an order?', 'Open the Support page and create a request with your order details.'],
      ],
      de: [['Wie wähle ich das passende Produkt?','Prüfen Sie Maße, Materialien und Optionen auf der Produktseite. Der Support hilft vor der Bestellung beim Vergleich.'],['Wie erfolgt die Lieferung?','Lieferart, Lieferzeit und Kosten werden beim Checkout und in den Bestelldetails angezeigt.'],['Kann ich eine Bestellung zurückgeben?','Die Rückgabemöglichkeit hängt vom Produkt und seinem Zustand ab. Sofern nicht anders angegeben, trägt der Kunde die Rücksendekosten.'],['Wie erhalte ich Hilfe?','Erstellen Sie auf der Supportseite eine Anfrage mit Ihren Bestelldaten.']],
      es: [['¿Cómo elijo el producto adecuado?','Consulta las medidas, materiales y opciones de la página del producto. Atención puede ayudarte antes de comprar.'],['¿Cómo se entregan los pedidos?','Los métodos, plazos y costes disponibles aparecen durante el pago y en los detalles del pedido.'],['¿Puedo devolver un pedido?','La posibilidad de devolución depende del producto y su estado. Salvo indicación contraria, el cliente paga el envío de devolución.'],['¿Cómo obtengo ayuda?','Crea una solicitud en la página de Atención con los datos de tu pedido.']],
      fr: [['Comment choisir le bon produit ?','Consultez les dimensions, matériaux et options sur la fiche produit. Le support peut vous aider avant la commande.'],['Comment les commandes sont-elles livrées ?','Les modes, délais et frais disponibles sont indiqués au paiement et dans le détail de la commande.'],['Puis-je retourner une commande ?','L’éligibilité dépend du produit et de son état. Sauf indication contraire, les frais de retour sont à la charge du client.'],['Comment obtenir de l’aide ?','Créez une demande sur la page Assistance avec les détails de votre commande.']],
    };
    return (rows[this.i18n.lang()] ?? rows['en']).map(([title,content],index) => ({id:-(index+1),title,content}));
  });
  protected readonly copy = computed(() => ({
    tr: {
      title: 'Sık sorulan sorular', categoryLead: 'Bu başlık altındaki en sık sorulan soruları tek yerde topladık.',
      lead: 'Önce merak ettiğin konuyu seç; sayfayı yenilediğinde aynı yerden devam edebilirsin.',
      allCategories: 'Tüm SSS kategorileri', category: 'SSS kategorisi', openCategory: 'Bu kategoriye ait soruları ve kısa yanıtları görüntüle.',
      noCategories: 'Henüz SSS kategorisi yok', noCategoriesLead: 'Kategoriler eklendiğinde bu alan otomatik dolacak.',
      noFaqs: 'Bu kategori için SSS bulunamadı', noFaqsLead: 'İçerik eklendiğinde burada otomatik görünecek.'
    },
    en: {
      title: 'Frequently asked questions', categoryLead: 'Find the most common questions for this topic in one place.',
      lead: 'Choose the topic you are curious about and return to the same page whenever you need.',
      allCategories: 'All FAQ categories', category: 'FAQ category', openCategory: 'View questions and concise answers in this category.',
      noCategories: 'No FAQ categories yet', noCategoriesLead: 'Categories will appear here when published.',
      noFaqs: 'No questions found in this category', noFaqsLead: 'Published answers will appear here automatically.'
    },
    de: {
      title: 'Häufig gestellte Fragen', categoryLead: 'Die häufigsten Fragen zu diesem Thema findest du hier an einem Ort.',
      lead: 'Wähle dein Thema aus und kehre später genau an diese Stelle zurück.',
      allCategories: 'Alle FAQ-Kategorien', category: 'FAQ-Kategorie', openCategory: 'Fragen und kurze Antworten dieser Kategorie anzeigen.',
      noCategories: 'Noch keine FAQ-Kategorien', noCategoriesLead: 'Veröffentlichte Kategorien erscheinen automatisch hier.',
      noFaqs: 'Keine Fragen in dieser Kategorie', noFaqsLead: 'Veröffentlichte Antworten erscheinen automatisch hier.'
    },
    es: {
      title: 'Preguntas frecuentes', categoryLead: 'Reunimos aquí las preguntas más frecuentes de este tema.',
      lead: 'Elige el tema que te interesa y vuelve a la misma página cuando quieras.',
      allCategories: 'Todas las categorías', category: 'Categoría de preguntas', openCategory: 'Consulta las preguntas y respuestas breves de esta categoría.',
      noCategories: 'Aún no hay categorías', noCategoriesLead: 'Las categorías publicadas aparecerán aquí.',
      noFaqs: 'No hay preguntas en esta categoría', noFaqsLead: 'Las respuestas publicadas aparecerán aquí.'
    },
    fr: {
      title: 'Questions fréquentes', categoryLead: 'Retrouvez ici les questions les plus fréquentes sur ce sujet.',
      lead: 'Choisissez le sujet qui vous intéresse et revenez ensuite au même endroit.',
      allCategories: 'Toutes les catégories', category: 'Catégorie FAQ', openCategory: 'Consultez les questions et réponses courtes de cette catégorie.',
      noCategories: 'Aucune catégorie pour le moment', noCategoriesLead: 'Les catégories publiées apparaîtront ici.',
      noFaqs: 'Aucune question dans cette catégorie', noFaqsLead: 'Les réponses publiées apparaîtront ici.'
    }
  })[this.i18n.lang()]);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      void this.load(params.get('slug') ?? '');
    });
  }

  private async load(slug: string): Promise<void> {
    this.slug.set(slug);
    this.category.set(null);
    this.categories.set([]);
    this.faqs.set([]);

    if (!slug) {
      const categories = await this.content.faqCategories();
      const seen = new Set<string>();
      this.categories.set(categories.filter((item) => {
        const safeSlug = String(item.slug ?? '').trim();
        if (!safeSlug || seen.has(safeSlug)) return false;
        seen.add(safeSlug);
        return true;
      }));
      this.seo.setPage({
        title: this.i18n.t('faqSeoTitle'),
        description: this.site.config().brandKey === 'bdsmbeds'
          ? 'Answers about BDSMBeds products, orders, delivery, returns and support.'
          : this.i18n.t('faqSeoDescription'),
        path: this.i18n.routePath('faq')
      });
      return;
    }

    const data = await this.content.faqs(slug);
    this.category.set(data.category);
    this.faqs.set(data.items);
    this.seo.setPage({
      title: `${data.category?.title || this.i18n.t('faq')} ${this.i18n.t('faqCategorySuffix')}`,
      description: `${data.category?.title || this.i18n.t('faq')} ${this.i18n.t('faqCategoryDescription')}`,
      path: this.i18n.routePath('faqCategory', { slug })
    });
  }
}
